import type { FC, ReactNode } from 'react'
import type { WorkItem } from '@/types'

import Link from 'next/link'
import { useRef } from 'react'
import { ArrowRight } from 'lucide-react'
import { gsap, MOTION_QUERIES, useGSAP } from '@/lib/motion'
import SectionHeading from '@/components/landing/section-heading'
import WorkLogCard from '@/components/landing/work-log-card'
import Sparkle from '@/components/landing/decor/sparkle'
import { WORKS_COPY } from '@/constants/content'

interface WorksSectionProps {
    works: Array<WorkItem>
}

const HIGHLIGHTS = 6

// WORKS on the one-page site: only the representative records (featured in the CMS), with
// VIEW MORE leading to the full, searchable archive at /works.
const WorksSection: FC<WorksSectionProps> = ({ works }): ReactNode => {
    const sectionRef = useRef<HTMLElement>(null)

    const numbered = works.map((work, index) => ({ work, number: index + 1 }))
    const featured = numbered.filter(entry => entry.work.featured)
    const highlights = (featured.length > 0 ? featured : numbered).slice(0, HIGHLIGHTS)

    useGSAP(() => {
        const section = sectionRef.current

        if (!section) return

        const q = gsap.utils.selector(section)
        const mm = gsap.matchMedia()

        mm.add({ desktop: MOTION_QUERIES.desktop, mobile: MOTION_QUERIES.mobile }, context => {
            const isDesktop = Boolean(context.conditions?.desktop)

            gsap.timeline({ scrollTrigger: { trigger: section, start: 'top 72%' } })
                .from(q('[data-heading-meta]'), { autoAlpha: 0, x: -16, duration: 1, stagger: 0.12, ease: 'power2.out' })
                .from(q('[data-char]'), { autoAlpha: 0, y: 40, filter: isDesktop ? 'blur(8px)' : 'blur(0px)', duration: 1.4, stagger: 0.08, ease: 'power3.out' }, 0)
                .from(q('[data-works-fade]'), { autoAlpha: 0, y: 20, duration: 1.2, stagger: 0.12, ease: 'power2.out' }, 0.4)

            q('[data-work-item]').forEach((item, index) => {
                gsap.from(item, {
                    autoAlpha: 0,
                    y: 48,
                    duration: 1.2,
                    delay: isDesktop ? (index % 3) * 0.12 : 0,
                    ease: 'power3.out',
                    scrollTrigger: { trigger: item, start: 'top 90%' }
                })
            })
        })
    }, { scope: sectionRef, dependencies: [highlights.length] })

    return (
        <section
            id='works'
            ref={sectionRef}
            className='relative overflow-hidden bg-[#dcd8e4] py-28 lg:py-36'
        >
            <div aria-hidden='true' className='pointer-events-none absolute inset-0 bg-[linear-gradient(to_right,rgba(29,42,51,0.04)_1px,transparent_1px),linear-gradient(to_bottom,rgba(29,42,51,0.04)_1px,transparent_1px)] bg-[size:72px_72px]' />

            <div className='relative mx-auto flex w-full max-w-7xl flex-col gap-12 px-5 lg:gap-16 lg:px-12'>
                <div className='flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between'>
                    <SectionHeading
                        index={WORKS_COPY.index}
                        kicker={WORKS_COPY.kicker}
                        title={WORKS_COPY.title}
                        ja={WORKS_COPY.ja}
                        titleClassName='whitespace-nowrap lg:text-[clamp(3rem,6vw,6.5rem)]'
                    />
                    <p data-works-fade className='max-w-md text-[15px] leading-[2] tracking-[0.05em] text-ink/80'>
                        {WORKS_COPY.lead}
                    </p>
                </div>

                {highlights.length > 0
                    ? (
                        <ul className='grid grid-cols-1 gap-x-5 gap-y-8 sm:grid-cols-2 lg:grid-cols-3 lg:gap-x-7 lg:gap-y-10'>
                            {highlights.map(entry => (
                                <li key={entry.work.id} data-work-item>
                                    <WorkLogCard work={entry.work} number={entry.number} />
                                </li>
                            ))}
                        </ul>
                    )
                    : (
                        <div className='flex flex-col items-center gap-4 rounded-[22px] border border-dashed border-ink/20 bg-mist/60 px-6 py-16 text-center'>
                            <Sparkle className='size-6 text-gold' />
                            <p className='text-sm tracking-[0.08em] text-ink/75'>{WORKS_COPY.empty}</p>
                        </div>
                    )}

                {works.length > 0 && (
                    <div data-works-fade className='flex justify-center'>
                        <Link
                            href='/works'
                            className='group/more inline-flex items-center gap-4 rounded-full bg-ink py-3 pl-6 pr-3 text-mist transition-[background-color,box-shadow] duration-500 hover:bg-night-soft hover:shadow-[0_0_0_6px_rgba(226,179,60,0.18)] focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-gold'
                        >
                            <Sparkle className='size-3 text-gold transition-transform duration-700 ease-[var(--ease-cinema)] group-hover/more:rotate-90' />
                            <span className='flex flex-col items-start leading-tight'>
                                <span className='font-mono text-xs tracking-[0.32em]'>{WORKS_COPY.more}</span>
                                <span className='text-[11px] tracking-[0.12em] text-mist/70'>{WORKS_COPY.moreJa}</span>
                            </span>
                            <span className='flex size-9 items-center justify-center rounded-full bg-mist/10'>
                                <ArrowRight className='size-4 transition-transform duration-500 group-hover/more:translate-x-0.5' />
                            </span>
                        </Link>
                    </div>
                )}
            </div>
        </section>
    )
}

export default WorksSection
